import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Contact } from './Contact';
import { Services } from './Services';
import { fadeInUp } from '../../lib/animations';
import { staggerContainer } from '../../lib/animations';
import { useLanguage } from '../../context/LanguageContext';

export const Sponsors = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  // Placeholder sponsor logos - user will update with real logos later
  const sponsors = [
    { 
      name: "Valiant Bank", 
      logo: "https://static.wixstatic.com/media/ccde95_f5cd4b2ecd724df2abb0e48eab919885~mv2.png/v1/fill/w_404,h_107,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Valiant.png",
      link: "http://valiant.ch/"
    },
    { 
      name: "Porsche Ober Zürichsee", 
      logo: "https://static.wixstatic.com/media/ccde95_ffbbe921788a4a8c85da61eb99741eea~mv2.avif/v1/fill/w_396,h_223,al_c,lg_1,q_80,enc_avif,quality_auto/34f97980-eb01-497f-b0b4-6a266f4ae3d2.avif",
      link: "https://dealer.porsche.com/ch/obererzuerichsee/de-CH"
    },
    { 
      name: "MTR AG", 
      logo: "https://static.wixstatic.com/media/ccde95_e7975b51ba764c2ca6797f5a02f0e09c~mv2.jpg/v1/fill/w_313,h_121,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/e379c9d6-8c0c-413c-b31d-61dd5aa592d2_JPG.jpg",
      link: "https://www.mtr-ag.ch/"
    },
    { 
      name: "DasTeam", 
      logo: "https://static.wixstatic.com/media/ccde95_b1c3993dcd4d4d079c2a21a013a7c51e~mv2.png/v1/fill/w_394,h_160,al_c,q_85,enc_avif,quality_auto/logo_deDasteam.png",
      link: "https://www.team.jobs/"
    },
    { 
      name: "Kirnbauer", 
      logo: "https://static.wixstatic.com/media/ccde95_44e3a79c6a9a4dde85772310fb9be3f2~mv2.png/v1/fill/w_370,h_160,al_c,lg_1,q_85,enc_avif,quality_auto/FN%20Kirnbauer%20Website.png",
      link: "https://kirnbauer.ch/"
    },
    {
      name: "Swiss Advisory Services",
      logo: "https://customer-assets-rejwkqb3.emergentagent.net/job_padel-arena-modern/artifacts/4rtp4qy8_ccbf5d35-135c-48c8-a4ce-c7cf7efa7dea.jpg",
      link: "https://www.swissadvisoryservices.com/"
    }
  ];

  return (
    <section id="sponsors" ref={ref} className="py-24 bg-slate-50" data-testid="sponsors-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="energy-bar w-24 mb-6 mx-auto"></div>
          <p className="overline mb-4">{t.sponsors.label}</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-6 tracking-tight">
            {t.sponsors.title}
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {t.sponsors.subtitle}
          </p>
        </motion.div>

        <motion.div 
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 items-center"
        >
          {sponsors.map((sponsor, index) => (
            <motion.a
              key={index}
              href={sponsor.link}
              target="_blank"
              rel="noopener noreferrer"
              variants={fadeInUp}
              className="flex items-center justify-center p-6 bg-white rounded-xl border border-slate-200 hover:border-blue-500 hover:shadow-lg transition-all duration-300 group min-h-[96px]"
              aria-label={sponsor.name}
            >
              {sponsor.textLogo ? (
                <div className="text-center leading-tight tracking-wide grayscale group-hover:grayscale-0 opacity-70 group-hover:opacity-100 transition-all duration-300">
                  <div className="text-slate-900 font-serif italic text-lg md:text-xl">{sponsor.textLine1}</div>
                  <div className="text-[#5B7FA8] font-sans font-semibold text-xs md:text-sm uppercase tracking-[0.2em] mt-1">{sponsor.textLine2}</div>
                </div>
              ) : (
                <img
                  src={sponsor.logo}
                  alt={sponsor.name}
                  className={`w-full h-auto object-contain grayscale group-hover:grayscale-0 transition-all duration-300 opacity-60 group-hover:opacity-100 ${
                    sponsor.name === 'Porsche Ober Zürichsee' ? 'max-h-24' : 'max-h-16'
                  }`}
                />
              )}
            </motion.a>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section
